import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { endpoints } from "@/api/client";
import { useOperatoreContext } from "@/lib/operatoreContext";
import { useAuth } from "@/lib/AuthProvider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/input";
import { registroIdentificativo } from "@/lib/registro";

export function OperatoreContextBar() {
  const auth = useAuth();
  const ctx = useOperatoreContext();

  const certsQ = useQuery({
    queryKey: ["certs", "ctx-bar"],
    queryFn: endpoints.certificates.list,
    enabled: auth.ready && auth.authenticated,
    staleTime: 30_000,
  });
  const certCf = certsQ.data?.[0]?.subject_cf?.trim() ?? "";
  const cf = certCf;

  useEffect(() => {
    if (certCf.length >= 11) {
      if (ctx.cf !== certCf) ctx.setCf(certCf);
      return;
    }
    if (!certsQ.isLoading && certsQ.isSuccess && ctx.cf) {
      ctx.setCf("");
    }
  }, [certCf, ctx.cf, ctx.setCf, certsQ.isLoading, certsQ.isSuccess]);

  const operatoriQ = useQuery({
    queryKey: ["ctx-operatori", cf],
    queryFn: () => endpoints.anagrafiche.operatori(cf),
    enabled: cf.length >= 11,
    retry: false,
  });
  const operatore = (Array.isArray(operatoriQ.data) ? operatoriQ.data[0] : undefined) as
    | { num_iscr?: string; denominazione?: string }
    | undefined;

  const iscr = operatore?.num_iscr ?? ctx.numIscr;

  useEffect(() => {
    if (operatore?.num_iscr) {
      ctx.setOperatore(operatore.num_iscr, operatore.denominazione ?? "");
    }
  }, [operatore?.num_iscr, operatore?.denominazione, ctx]);

  const sitiQ = useQuery({
    queryKey: ["ctx-siti", iscr],
    queryFn: () => endpoints.anagrafiche.siti(iscr),
    enabled: !!iscr,
    retry: false,
  });
  const siti = (Array.isArray(sitiQ.data) ? sitiQ.data : []) as Record<string, unknown>[];

  useEffect(() => {
    if (ctx.numIscrSito || siti.length === 0) return;
    const s = siti[0];
    const id = String(s.num_iscr_sito ?? s.identificativo ?? s.id ?? "");
    if (!id) return;
    const label = String(s.denominazione ?? s.nome ?? id);
    ctx.setSito(id, label);
  }, [siti, ctx.numIscrSito, ctx.setSito]);

  const registriQ = useQuery({
    queryKey: ["ctx-registri", iscr, ctx.numIscrSito],
    queryFn: () => endpoints.registri.lista(iscr, ctx.numIscrSito),
    enabled: !!iscr && !!ctx.numIscrSito,
    retry: false,
  });
  const registri = (Array.isArray(registriQ.data) ? registriQ.data : []) as Record<string, unknown>[];

  return (
    <div className="space-y-2 border-b px-3 py-3 text-xs">
      <div>
        <Label className="text-[10px] uppercase text-muted-foreground">CF / P.IVA</Label>
        <Input
          className="mt-1 h-8 text-xs bg-muted/40"
          value={cf}
          readOnly
          aria-readonly
          placeholder="Da certificato P12"
        />
        {!certCf && !certsQ.isLoading && (
          <p className="mt-1 text-[10px] text-muted-foreground">
            <Link to="/impostazioni" className="underline hover:text-foreground">
              Carica il certificato P12
            </Link>{" "}
            per impostare l&apos;operatore.
          </p>
        )}
      </div>
      {operatore?.denominazione && (
        <p className="truncate text-[11px] text-muted-foreground" title={operatore.denominazione}>
          {operatore.denominazione}
        </p>
      )}
      <div>
        <Label className="text-[10px] uppercase text-muted-foreground">Unità locale</Label>
        <select
          className="mt-1 h-8 w-full rounded-md border bg-background px-2 text-xs"
          value={ctx.numIscrSito}
          onChange={(e) => {
            const opt = e.target.selectedOptions[0];
            ctx.setSito(e.target.value, opt?.text ?? "");
          }}
        >
          <option value="">— seleziona sito —</option>
          {siti.map((s, i) => {
            const id = String(s.num_iscr_sito ?? s.identificativo ?? s.id ?? i);
            const label = String(s.denominazione ?? s.nome ?? id);
            return (
              <option key={id} value={id}>
                {label}
              </option>
            );
          })}
        </select>
      </div>
      {ctx.numIscrSito && (
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground">Registro</Label>
          <select
            className="mt-1 h-8 w-full rounded-md border bg-background px-2 text-xs"
            value={ctx.registroId}
            onChange={(e) => ctx.setRegistroId(e.target.value)}
          >
            <option value="">— seleziona registro —</option>
            {registri.map((r, i) => {
              const id = registroIdentificativo(r);
              return (
                <option key={id || String(i)} value={id}>
                  {registroLabel(r)}
                </option>
              );
            })}
          </select>
        </div>
      )}
    </div>
  );
}

function registroLabel(r: Record<string, unknown>): string {
  const id = registroIdentificativo(r);
  const desc = r.descrizione ?? r.Descrizione;
  return desc ? `${String(desc)} (${id.slice(0, 8)}…)` : `${id.slice(0, 12)}…`;
}
